/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Control, useController } from "react-hook-form";
import FormSelect from "./FormSelect";

export interface SelectFieldProps {
  label?: string;
  name: string;
  control: Control<any>;
  isRequired?: boolean;
  placeholder?: string;
  options?: any[];
  withSearch?: boolean;
  withCheckbox?: boolean;
  isMultiple?: boolean;
  keepOpen?: boolean;
  onChange?: (e: SelectOptionType[]) => void;
  disabled?: boolean;
  dropdownHeight?: string;
  itemRenderer?: (item: SelectOptionType) => JSX.Element;
  fetchOptions?: (
    searchTerm?: string,
    page?: number
  ) => Promise<{ data: SelectOptionType[]; totalPages: number }>;
}

export type SelectOptionType = {
  label: string | React.ReactNode;
  value: any;
  disabled?: boolean;
  other?: any;
};

export const SelectField = memo(
  ({
    label,
    name,
    control,
    isRequired = false,
    withSearch = false,
    withCheckbox = false,
    isMultiple = false,
    keepOpen: keepOpenProp = false,
    placeholder,
    options,
    onChange: onChangeParent,
    disabled,
    itemRenderer,
    fetchOptions,
    dropdownHeight = "300px",
  }: SelectFieldProps) => {
    const {
      field: { onChange, value },
      fieldState: { error },
    } = useController({ name, control });

    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef<HTMLDivElement>(null);

    const [internalOptions, setInternalOptions] = useState<SelectOptionType[]>(
      () => (fetchOptions ? [] : options || [])
    );
    const [totalPages, setTotalPages] = useState(() =>
      fetchOptions ? 0 : options && options.length > 0 ? 1 : 0
    );
    const [initialFetchDone, setInitialFetchDone] = useState(() =>
      fetchOptions ? false : true
    );
    const [isLoadingInitial, setIsLoadingInitial] = useState(false);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          selectRef.current &&
          !selectRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const prevFetchOptionsRef = useRef(fetchOptions);

    useEffect(() => {
      if (prevFetchOptionsRef.current !== fetchOptions) {
        if (fetchOptions) {
          setInternalOptions([]);
          setTotalPages(0);
          setInitialFetchDone(false);
          setIsLoadingInitial(false);
          lastSearchTermRef.current = "";
        } else {
          setInternalOptions(options || []);
          setTotalPages(options && options.length > 0 ? 1 : 0);
          setInitialFetchDone(true);
          setIsLoadingInitial(false);
        }
      } else if (!fetchOptions) {
        setInternalOptions(options || []);
        setTotalPages(options && options.length > 0 ? 1 : 0);
        setInitialFetchDone(true);
      }
      prevFetchOptionsRef.current = fetchOptions;
    }, [options, fetchOptions]);

    useEffect(() => {
      if (!isOpen && fetchOptions) {
        setInitialFetchDone(false);
      }
    }, [isOpen, fetchOptions]);

    useEffect(() => {
      if (
        isOpen &&
        fetchOptions &&
        !initialFetchDone &&
        internalOptions?.length === 0
      ) {
        setIsLoadingInitial(true);
        fetchOptions("", 1)
          .then((response) => {
            setInternalOptions(response.data);
            setTotalPages(response.totalPages);
            setInitialFetchDone(true);
          })
          .catch(() => {})
          .finally(() => {
            setIsLoadingInitial(false);
          });
      }
    }, [isOpen, fetchOptions, initialFetchDone, internalOptions?.length]);

    const lastSearchTermRef = useRef<string>("");

    const handleFetchOptionsForFormSelect = useCallback(
      async (
        searchTerm: string,
        page: number
      ): Promise<{ data: SelectOptionType[]; totalPages: number }> => {
        if (!fetchOptions) {
          return Promise.resolve({ data: [], totalPages });
        }
        setIsLoadingInitial(true);
        try {
          const response = await fetchOptions(searchTerm, page);
          setInternalOptions((prevOptions) => {
            if (page === 1 || searchTerm !== lastSearchTermRef.current) {
              return response.data;
            } else {
              const existingValues = new Set(prevOptions.map((o) => o.value));
              const newDataOnly = response.data.filter(
                (o) => !existingValues.has(o.value)
              );
              return [...prevOptions, ...newDataOnly];
            }
          });
          setTotalPages(response.totalPages);
          lastSearchTermRef.current = searchTerm;
          return response;
        } catch (error) {
          console.error("Error in handleFetchOptionsForFormSelect:", error);
          throw error;
        } finally {
          setIsLoadingInitial(false);
        }
      },
      [fetchOptions]
    );

    const handleWrapperClick = () => {
      if (!disabled) {
        setIsOpen(true);
      }
    };

    const effectiveKeepOpen = isOpen && keepOpenProp;

    return (
      <div ref={selectRef} onClick={handleWrapperClick}>
        <FormSelect
          options={fetchOptions ? internalOptions : options || []}
          isMultiple={isMultiple}
          withCheckbox={withCheckbox}
          withSearch={withSearch}
          keepOpen={effectiveKeepOpen}
          placeholder={placeholder}
          value={value ?? []}
          formLabel={label}
          isRequired={isRequired}
          onChange={(e: SelectOptionType[]) => {
            onChange?.(e);
            onChangeParent?.(e);
          }}
          hasError={!!error}
          errorMessage={error?.message}
          itemRenderer={itemRenderer}
          disabled={disabled}
          fetchOptions={
            fetchOptions ? handleFetchOptionsForFormSelect : undefined
          }
          totalPages={totalPages}
          loading={isLoadingInitial}
          dropdownHeight={dropdownHeight}
        />
      </div>
    );
  }
);

export default SelectField;
